import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Check, Copy, KeyRound, Loader, Pencil, PlugZap, Save, X } from 'lucide-react';
import EmptyState from '../governed-mcp-gateway/components/EmptyState';
import { StatusBadge } from '../governed-mcp-gateway/components/StatusBadges';
import { ghostBtn, HelpHint, panelClass, primaryBtn } from '../governed-mcp-gateway/components/GatewayUi';
import {
  anthropicBaseUrl,
  anthropicFamilySelected,
  apiTypeLabel,
  APP_CONNECTORS,
  customFamilySelected,
  GOVERNANCE_HELP,
  isAppCompatible,
  LLM_GATEWAY_PATHS,
  openaiBaseUrl,
  openaiFamilySelected,
  preferredAppApiType,
  providerLabel,
} from './llmGatewayConstants';
import { logLlmGateway } from './llmGatewayLogger';
import { useLlmGatewayPage } from './LlmGatewayScreen';
import { firstError, validateGovernance } from './llmGatewayForm';
import { useLlmGateway } from './llmGatewayStore';
import type { AppConnector, AppConnectorId, GatewayApiType, GovernanceSettings, LlmGateway } from './llmGatewayTypes';
import GovernancePoliciesEditor from './components/GovernancePoliciesEditor';

async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

export default function GatewayDetailPage() {
  const { isLight, onToast } = useLlmGatewayPage();
  const { gatewayId } = useParams<{ gatewayId: string }>();
  const navigate = useNavigate();
  const { getGateway, getModel, generateApiKey, updateGovernance, testConnection } = useLlmGateway();
  const gateway = gatewayId ? getGateway(gatewayId) : undefined;
  const model = gateway ? getModel(gateway.modelId) : undefined;
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [appId, setAppId] = useState<AppConnectorId | null>(null);
  const [editingGovernance, setEditingGovernance] = useState(false);
  const [governanceDraft, setGovernanceDraft] = useState<GovernanceSettings | null>(null);
  const [govErrors, setGovErrors] = useState<Record<string, string>>({});

  if (!gateway) {
    return (
      <EmptyState
        isLight={isLight}
        title="Gateway not found"
        description="This gateway is missing or was deleted."
        action={
          <button type="button" className={primaryBtn()} onClick={() => navigate(LLM_GATEWAY_PATHS.list)}>
            Back to list
          </button>
        }
      />
    );
  }

  const onCopy = async (label: string, value: string) => {
    const ok = await copyText(value);
    if (ok) {
      onToast('success', `${label} copied.`);
      logLlmGateway('app', 'copy_value', { label, id: gateway.id });
    } else {
      onToast('error', `Could not copy ${label.toLowerCase()}.`);
      logLlmGateway('warning', 'copy_failed', { label, id: gateway.id });
    }
  };

  const onGenerateKey = async () => {
    try {
      const key = await generateApiKey(gateway.id);
      if (!key) {
        onToast('error', 'Could not generate an API key.');
        return;
      }
      setRevealedKey(key);
      onToast('success', 'API key generated. Copy it now; it is shown once.');
    } catch (error) {
      onToast('error', error instanceof Error ? error.message : 'Could not generate an API key.');
    }
  };

  const onTest = async () => {
    setTesting(true);
    logLlmGateway('app', 'test_connection_start', { id: gateway.id });
    try {
      const result = await testConnection(gateway.id);
      if (result.ok) {
        onToast('success', result.message);
        logLlmGateway('app', 'test_connection_ok', { id: gateway.id });
      } else {
        onToast('error', result.message);
        logLlmGateway('warning', 'test_connection_blocked', { id: gateway.id, status: gateway.status });
      }
    } catch (error) {
      onToast('error', error instanceof Error ? error.message : 'Connection test failed.');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-5">
      <GatewaySummary gateway={gateway} modelName={model?.name ?? gateway.modelId} isLight={isLight} onEdit={() => navigate(LLM_GATEWAY_PATHS.edit(gateway.id))} />
      <ConnectionPanel
        gateway={gateway}
        revealedKey={revealedKey}
        testing={testing}
        isLight={isLight}
        onCopy={onCopy}
        onGenerateKey={onGenerateKey}
        onTest={onTest}
      />
      <GovernancePanel
        gateway={gateway}
        draft={governanceDraft}
        editing={editingGovernance}
        errors={govErrors}
        isLight={isLight}
        onStart={() => {
          setGovernanceDraft({ ...gateway.governance });
          setGovErrors({});
          setEditingGovernance(true);
          logLlmGateway('app', 'governance_edit_open', { id: gateway.id });
        }}
        onCancel={() => {
          setEditingGovernance(false);
          setGovernanceDraft(null);
          setGovErrors({});
        }}
        onChange={(next) => {
          setGovernanceDraft(next);
          setGovErrors({});
        }}
        onSave={() => {
          if (!governanceDraft) return;
          const errors = validateGovernance(governanceDraft);
          if (Object.keys(errors).length) {
            setGovErrors(errors);
            onToast('error', firstError(errors) ?? 'Complete governance settings before saving.');
            return;
          }
          void (async () => {
            try {
              const saved = await updateGovernance(gateway.id, governanceDraft);
              if (!saved) {
                onToast('error', 'Could not update governance policies.');
                return;
              }
              setEditingGovernance(false);
              setGovernanceDraft(null);
              onToast('success', 'Governance policies updated.');
            } catch (error) {
              onToast('error', error instanceof Error ? error.message : 'Could not update governance policies.');
            }
          })();
        }}
      />
      <ConnectApps
        gateway={gateway}
        revealedKey={revealedKey}
        appId={appId}
        isLight={isLight}
        onSelect={setAppId}
        onCopy={onCopy}
      />
    </div>
  );
}

function GatewaySummary({
  gateway,
  modelName,
  isLight,
  onEdit,
}: {
  gateway: LlmGateway;
  modelName: string;
  isLight: boolean;
  onEdit: () => void;
}) {
  return (
    <section className={`${panelClass(isLight)} p-5`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>Gateway Details</h2>
          <p className={`mt-1 text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>Published alias and bound model.</p>
        </div>
        <button type="button" className={ghostBtn(isLight)} onClick={onEdit}>
          <Pencil size={13} />
          Edit
        </button>
      </div>
      <dl className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
        <SummaryItem label="Gateway Name" value={gateway.name} isLight={isLight} />
        <div>
          <dt className={`text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>Status</dt>
          <dd className="mt-1">
            <StatusBadge status={gateway.status} />
          </dd>
        </div>
        <SummaryItem label="Registered Model" value={modelName} isLight={isLight} />
        <SummaryItem label="Provider" value={providerLabel(gateway.provider)} isLight={isLight} />
        <SummaryItem label="Gateway API Type" value={gateway.apiTypes.map(apiTypeLabel).join(', ')} isLight={isLight} />
        <div>
          <dt className={`text-xs flex items-center gap-1 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
            Public Model Alias
            <HelpHint text={GOVERNANCE_HELP.publicAlias} />
          </dt>
          <dd className={`mt-1 font-mono text-xs ${isLight ? 'text-neutral-800' : 'text-neutral-200'}`}>{gateway.publicAlias}</dd>
        </div>
      </dl>
    </section>
  );
}

function SummaryItem({ label, value, isLight }: { label: string; value: string; isLight: boolean }) {
  return (
    <div>
      <dt className={`text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{label}</dt>
      <dd className={`mt-1 font-medium ${isLight ? 'text-neutral-800' : 'text-neutral-100'}`}>{value}</dd>
    </div>
  );
}

function ConnectionPanel({
  gateway,
  revealedKey,
  testing,
  isLight,
  onCopy,
  onGenerateKey,
  onTest,
}: {
  gateway: LlmGateway;
  revealedKey: string | null;
  testing: boolean;
  isLight: boolean;
  onCopy: (label: string, value: string) => Promise<void>;
  onGenerateKey: () => void;
  onTest: () => void;
}) {
  const urls = [
    openaiFamilySelected(gateway.apiTypes) ? { label: 'OpenAI-compatible Base URL', value: openaiBaseUrl(gateway.id) } : null,
    anthropicFamilySelected(gateway.apiTypes) ? { label: 'Anthropic-compatible Base URL', value: anthropicBaseUrl(gateway.id) } : null,
    customFamilySelected(gateway.apiTypes) && gateway.customEndpoint
      ? { label: 'Custom API endpoint', value: gateway.customEndpoint }
      : null,
  ].filter(Boolean) as { label: string; value: string }[];

  return (
    <section className={`${panelClass(isLight)} p-5`}>
      <h2 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>Connection Information</h2>
      <p className={`mt-1 text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
        URLs are shown only for the API families selected on this gateway.
      </p>
      <div className="mt-4 space-y-3">
        {urls.map((item) => (
          <div key={item.label} className={`rounded-xl border p-3.5 ${isLight ? 'border-neutral-200' : 'border-neutral-800'}`}>
            <p className={`text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{item.label}</p>
            <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
              <code className={`text-xs break-all ${isLight ? 'text-neutral-800' : 'text-neutral-200'}`}>{item.value}</code>
              <button type="button" className={ghostBtn(isLight)} onClick={() => void onCopy('URL', item.value)}>
                <Copy size={13} />
                Copy URL
              </button>
            </div>
          </div>
        ))}
        <div className={`rounded-xl border p-3.5 ${isLight ? 'border-neutral-200' : 'border-neutral-800'}`}>
          <p className={`text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>API key</p>
          <p className={`mt-1 font-mono text-xs ${isLight ? 'text-neutral-800' : 'text-neutral-200'}`}>
            {revealedKey ?? (gateway.hasApiKey ? `sk-selfic-••••••••${gateway.apiKeyLast4 ?? '••••'}` : 'No key generated yet')}
          </p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button type="button" className={ghostBtn(isLight)} onClick={() => void onCopy('Model alias', gateway.publicAlias)}>
          <Copy size={13} />
          Copy Model Alias
        </button>
        <button type="button" className={ghostBtn(isLight)} onClick={() => void onGenerateKey()}>
          <KeyRound size={13} />
          Generate API Key
        </button>
        {revealedKey ? (
          <button type="button" className={ghostBtn(isLight)} onClick={() => void onCopy('API key', revealedKey)}>
            <Copy size={13} />
            Copy API Key
          </button>
        ) : null}
        <button type="button" className={primaryBtn()} disabled={testing} onClick={() => void onTest()}>
          {testing ? <Loader size={13} className="animate-spin" /> : <PlugZap size={13} />}
          Test Connection
        </button>
      </div>
    </section>
  );
}

function GovernancePanel({
  gateway,
  draft,
  editing,
  errors,
  isLight,
  onStart,
  onCancel,
  onChange,
  onSave,
}: {
  gateway: LlmGateway;
  draft: GovernanceSettings | null;
  editing: boolean;
  errors: Record<string, string>;
  isLight: boolean;
  onStart: () => void;
  onCancel: () => void;
  onChange: (next: GovernanceSettings) => void;
  onSave: () => void;
}) {
  return (
    <section className={`${panelClass(isLight)} p-5`}>
      <GovernancePoliciesEditor
        governance={draft ?? gateway.governance}
        errors={errors}
        isLight={isLight}
        readOnly={!editing}
        onChange={onChange}
        action={
          editing ? (
            <>
              <button type="button" className={ghostBtn(isLight)} onClick={onCancel}>
                <X size={13} />
                Cancel
              </button>
              <button type="button" className={primaryBtn()} onClick={onSave}>
                <Save size={13} />
                Save policies
              </button>
            </>
          ) : (
            <button type="button" className={ghostBtn(isLight)} onClick={onStart}>
              <Pencil size={13} />
              Update
            </button>
          )
        }
      />
    </section>
  );
}

function ConnectApps({
  gateway,
  revealedKey,
  appId,
  isLight,
  onSelect,
  onCopy,
}: {
  gateway: LlmGateway;
  revealedKey: string | null;
  appId: AppConnectorId | null;
  isLight: boolean;
  onSelect: (id: AppConnectorId) => void;
  onCopy: (label: string, value: string) => Promise<void>;
}) {
  const selected = APP_CONNECTORS.find((app) => app.id === appId);
  const config = useMemo(
    () => (selected ? buildAppConfig(selected, gateway, revealedKey) : null),
    [gateway, revealedKey, selected]
  );

  return (
    <section className={`${panelClass(isLight)} p-5`}>
      <h2 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>Connect to Application</h2>
      <p className={`mt-1 text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
        Applications that cannot speak the selected gateway API stay disabled.
      </p>
      <div className="mt-4 grid sm:grid-cols-2 xl:grid-cols-5 gap-3">
        {APP_CONNECTORS.map((app) => {
          const compatible = isAppCompatible(app, gateway.apiTypes);
          const active = app.id === appId;
          return (
            <button
              key={app.id}
              type="button"
              disabled={!compatible}
              onClick={() => onSelect(app.id)}
              className={`text-left rounded-2xl border p-3.5 transition-colors ${
                !compatible
                  ? isLight
                    ? 'border-neutral-200 bg-neutral-50 opacity-60 cursor-not-allowed'
                    : 'border-neutral-800 bg-neutral-950/40 opacity-50 cursor-not-allowed'
                  : active
                    ? isLight
                      ? 'border-indigo-300 bg-indigo-50/70'
                      : 'border-indigo-500/40 bg-indigo-500/10'
                    : isLight
                      ? 'border-neutral-200 bg-white hover:border-neutral-300'
                      : 'border-neutral-800 bg-neutral-900 hover:border-neutral-700'
              }`}
            >
              <p className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>{app.name}</p>
              <p className={`mt-1 text-xs ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>{app.description}</p>
              {!compatible ? (
                <p className="mt-2 text-[11px] text-amber-600">Needs {app.requiredApiTypes.map(apiTypeLabel).join(' or ')}.</p>
              ) : (
                <p className={`mt-2 text-[11px] ${isLight ? 'text-emerald-700' : 'text-emerald-300'}`}>
                  <Check size={11} className="inline mr-1" />
                  Compatible
                </p>
              )}
            </button>
          );
        })}
      </div>

      {selected && config ? (
        <div className={`mt-5 rounded-2xl border p-4 ${isLight ? 'border-neutral-200 bg-neutral-50/70' : 'border-neutral-800 bg-neutral-950/50'}`}>
          <h3 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>{selected.name} configuration</h3>
          <dl className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
            <SummaryItem label="Compatible API type" value={apiTypeLabel(config.apiType)} isLight={isLight} />
            <SummaryItem label="Base URL" value={config.baseUrl} isLight={isLight} />
            <SummaryItem label="API key" value={config.apiKey} isLight={isLight} />
            <SummaryItem label="Model alias" value={gateway.publicAlias} isLight={isLight} />
          </dl>
          <pre className={`mt-4 overflow-x-auto rounded-xl p-3 text-[11px] leading-relaxed ${isLight ? 'bg-white text-neutral-700 border border-neutral-200' : 'bg-neutral-900 text-neutral-200 border border-neutral-800'}`}>
            {config.snippet}
          </pre>
          <button type="button" className={`${ghostBtn(isLight)} mt-3`} onClick={() => void onCopy('Configuration', config.snippet)}>
            <Copy size={13} />
            Copy configuration
          </button>
        </div>
      ) : null}
    </section>
  );
}

function buildAppConfig(app: AppConnector, gateway: LlmGateway, revealedKey: string | null) {
  const apiType = preferredAppApiType(app, gateway.apiTypes) ?? gateway.apiTypes[0];
  const baseUrl = resolveAppBaseUrl(apiType, gateway);
  const apiKey = revealedKey ?? 'sk-selfic-••••••••••••••••';
  return {
    apiType,
    baseUrl,
    apiKey,
    snippet: appSnippet(app.id, { baseUrl, apiKey, model: gateway.publicAlias, apiType }),
  };
}

function resolveAppBaseUrl(apiType: GatewayApiType, gateway: LlmGateway): string {
  if (apiType === 'anthropic_messages') return anthropicBaseUrl(gateway.id);
  if (apiType === 'custom') return gateway.customEndpoint || openaiBaseUrl(gateway.id);
  return openaiBaseUrl(gateway.id);
}

function appSnippet(
  appId: AppConnectorId,
  values: { baseUrl: string; apiKey: string; model: string; apiType: GatewayApiType }
): string {
  if (appId === 'claude_code') {
    return `ANTHROPIC_BASE_URL=${values.baseUrl}\nANTHROPIC_API_KEY=${values.apiKey}\nANTHROPIC_MODEL=${values.model}`;
  }
  if (appId === 'cursor' || appId === 'github_copilot') {
    return `OPENAI_BASE_URL=${values.baseUrl}\nOPENAI_API_KEY=${values.apiKey}\nOPENAI_MODEL=${values.model}`;
  }
  if (appId === 'langchain') {
    return `from langchain_openai import ChatOpenAI\n\nllm = ChatOpenAI(\n  model="${values.model}",\n  base_url="${values.baseUrl}",\n  api_key="${values.apiKey}",\n)`;
  }
  return `curl ${values.baseUrl}${apiPath(values.apiType)} \\\n  -H "Authorization: Bearer ${values.apiKey}" \\\n  -H "Content-Type: application/json"`;
}

function apiPath(apiType: GatewayApiType): string {
  if (apiType === 'openai_chat_completions') return '/chat/completions';
  if (apiType === 'openai_responses') return '/responses';
  if (apiType === 'openai_embeddings') return '/embeddings';
  if (apiType === 'anthropic_messages') return '/v1/messages';
  if (apiType === 'legacy_completions') return '/completions';
  return '';
}
